import React, { useEffect, useState } from 'react';
import {
  Card,
  Button,
  Form,
  Input,
  Space,
  Alert,
  Typography,
  Divider,
  Row,
  Col,
  Steps,
  App,
  Tag,
  Modal,
} from 'antd';
import {
  SafetyOutlined,
  QrcodeOutlined,
  LockOutlined,
  KeyOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { adminAPI } from '../services/api';

const { Title, Paragraph, Text } = Typography;
const { Step } = Steps;

const TwoFactorAuth: React.FC = () => {
  const { message, modal } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [enabled, setEnabled] = useState(false);

  // 绑定流程状态
  const [bindStep, setBindStep] = useState(0); // 0: 输入密码, 1: 扫描二维码, 2: 验证码确认
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [secret, setSecret] = useState<string>('');
  const [showBindModal, setShowBindModal] = useState(false);

  const [passwordForm] = Form.useForm();
  const [verifyForm] = Form.useForm();
  const [unbindForm] = Form.useForm();

  // 检查 2FA 状态
  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    setChecking(true);
    try {
      const response = await adminAPI.get2FAStatus();
      setEnabled(response.data.enabled);
    } catch (error: any) {
      message.error('获取 2FA 状态失败: ' + (error.response?.data?.error || error.message));
    } finally {
      setChecking(false);
    }
  };

  // 开始绑定 - 生成密钥
  const handleStartBind = async (values: { password: string }) => {
    setLoading(true);
    try {
      const response = await adminAPI.bind2FA(values.password);
      const { totp } = response.data;

      setSecret(totp.secret);
      setQrCodeUrl(totp.qrCode);
      setBindStep(1); // 进入扫描二维码步骤
      message.success('密钥已生成，请扫描二维码');
    } catch (error: any) {
      message.error('生成密钥失败: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 进入验证步骤
  const handleNextToVerify = () => {
    setBindStep(2);
  };

  // 完成绑定 - 验证码确认
  const handleFinishBind = async (values: { totpCode: string }) => {
    setLoading(true);
    try {
      // 这里实际上后端已经绑定了，我们只是验证用户是否正确配置
      // 可以调用登录接口或专门的验证接口来确认
      message.success('2FA 绑定成功！');

      // 重置状态
      setShowBindModal(false);
      setBindStep(0);
      setSecret('');
      setQrCodeUrl('');
      passwordForm.resetFields();
      verifyForm.resetFields();

      // 刷新状态
      checkStatus();
    } catch (error: any) {
      message.error('验证失败: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 取消绑定
  const handleCancelBind = () => {
    modal.confirm({
      title: '确认取消绑定',
      content: '取消后，刚才生成的密钥将失效。是否继续？',
      okText: '确定',
      cancelText: '返回',
      onOk: () => {
        setShowBindModal(false);
        setBindStep(0);
        setSecret('');
        setQrCodeUrl('');
        passwordForm.resetFields();
        verifyForm.resetFields();

        // 由于后端已经设置了 totp_enabled=1，需要清理
        // 这里可能需要一个专门的取消接口
        message.info('已取消绑定');
        checkStatus();
      },
    });
  };

  // 解绑 2FA
  const handleUnbind = async (values: { password: string; totpCode: string }) => {
    console.log('[2FA 解绑] 表单提交，values:', values);
    modal.confirm({
      title: '确认解绑 2FA',
      content: '解绑后，您将只需要用户名和密码即可登录。确定要解绑吗？',
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        console.log('[2FA 解绑] 用户确认解绑，准备调用后端接口');
        setLoading(true);
        try {
          console.log('[2FA 解绑] 调用 adminAPI.unbind2FA');
          await adminAPI.unbind2FA(values.password, values.totpCode);
          console.log('[2FA 解绑] 解绑成功');
          message.success('2FA 已解绑');
          unbindForm.resetFields();
          checkStatus();
        } catch (error: any) {
          console.error('[2FA 解绑] 解绑失败:', error);
          message.error('解绑 2FA 失败: ' + (error.response?.data?.error || error.message));
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // 复制密钥
  const handleCopySecret = () => {
    navigator.clipboard.writeText(secret);
    message.success('密钥已复制到剪贴板');
  };

  // 打开绑定弹窗
  const openBindModal = () => {
    setShowBindModal(true);
    setBindStep(0);
  };

  if (checking) {
    return (
      <Card loading={true}>
        <p>正在加载...</p>
      </Card>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* 页面标题 */}
          <div>
            <Title level={3}>
              <SafetyOutlined /> 双因素认证 (2FA)
            </Title>
            <Paragraph type="secondary">
              双因素认证可以为您的账号提供额外的安全保护。启用后，登录时需要输入 Google
              Authenticator 生成的验证码。
            </Paragraph>
          </div>

          <Divider />

          {/* 当前状态 */}
          <Card bordered={false} style={{ backgroundColor: '#f5f5f5' }}>
            <Row align="middle" gutter={16}>
              <Col>
                <Text strong style={{ fontSize: 16 }}>当前状态：</Text>
              </Col>
              <Col>
                {enabled ? (
                  <Tag icon={<CheckCircleOutlined />} color="success" style={{ fontSize: 14, padding: '4px 12px' }}>
                    已启用
                  </Tag>
                ) : (
                  <Tag icon={<CloseCircleOutlined />} color="default" style={{ fontSize: 14, padding: '4px 12px' }}>
                    未启用
                  </Tag>
                )}
              </Col>
              <Col flex="auto">
                <Text type="secondary">
                  {enabled
                    ? '您的账号已绑定 Google Authenticator，登录时需要验证码。'
                    : '您的账号未绑定 2FA，可以直接使用用户名和密码登录。'}
                </Text>
              </Col>
            </Row>
          </Card>

          {/* 绑定 2FA */}
          {!enabled && (
            <Card
              title={
                <>
                  <QrcodeOutlined /> 绑定 Google Authenticator
                </>
              }
              bordered={false}
              style={{ backgroundColor: '#fafafa' }}
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Alert
                  message="绑定前准备"
                  description={
                    <div>
                      <p>1. 在手机上安装 Google Authenticator 应用</p>
                      <p>2. 点击下方按钮开始绑定流程</p>
                      <p>3. 扫描二维码或手动输入密钥</p>
                      <p>4. 输入验证码确认绑定</p>
                    </div>
                  }
                  type="info"
                  showIcon
                />

                <Button type="primary" size="large" icon={<SafetyOutlined />} onClick={openBindModal}>
                  开始绑定 2FA
                </Button>
              </Space>
            </Card>
          )}

          {/* 解绑 2FA */}
          {enabled && (
            <Card
              title={
                <>
                  <KeyOutlined /> 解绑 Google Authenticator
                </>
              }
              bordered={false}
              style={{ backgroundColor: '#fff7e6' }}
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Alert
                  message="解绑说明"
                  description="解绑后，您将只需要用户名和密码即可登录，但安全性会降低。"
                  type="warning"
                  showIcon
                />

                <Form
                  form={unbindForm}
                  onFinish={handleUnbind}
                  onFinishFailed={() => message.error('请先填写密码和验证码')}
                  layout="vertical"
                >
                  <Form.Item
                    label="账号密码"
                    name="password"
                    rules={[{ required: true, message: '请输入密码' }]}
                  >
                    <Input.Password
                      prefix={<LockOutlined />}
                      placeholder="请输入密码"
                      size="large"
                    />
                  </Form.Item>

                  <Form.Item
                    label="当前验证码"
                    name="totpCode"
                    rules={[
                      { required: true, message: '请输入验证码' },
                      { pattern: /^\d{6}$/, message: '验证码为 6 位数字' },
                    ]}
                  >
                    <Input
                      prefix={<KeyOutlined />}
                      placeholder="请输入 Google Authenticator 当前验证码"
                      maxLength={6}
                      size="large"
                    />
                  </Form.Item>

                  <Form.Item>
                    <Button type="primary" danger htmlType="submit" loading={loading} size="large">
                      解绑 2FA
                    </Button>
                  </Form.Item>
                </Form>
              </Space>
            </Card>
          )}
        </Space>
      </Card>

      {/* 绑定流程弹窗 */}
      <Modal
        title="绑定 Google Authenticator"
        open={showBindModal}
        onCancel={handleCancelBind}
        footer={null}
        width={700}
        destroyOnClose
      >
        <Steps current={bindStep} style={{ marginBottom: 24 }}>
          <Step title="验证密码" />
          <Step title="扫描二维码" />
          <Step title="验证绑定" />
        </Steps>

        {/* 步骤1: 输入密码 */}
        {bindStep === 0 && (
          <Form form={passwordForm} onFinish={handleStartBind} layout="vertical">
            <Alert
              message="身份验证"
              description="为了安全，请先输入您的账号密码进行身份验证。"
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Form.Item
              label="账号密码"
              name="password"
              rules={[{ required: true, message: '请输入密码' }]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="请输入密码以验证身份"
                size="large"
              />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={loading} size="large">
                  下一步
                </Button>
                <Button onClick={handleCancelBind} size="large">
                  取消
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}

        {/* 步骤2: 扫描二维码 */}
        {bindStep === 1 && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Alert
              message="重要提示"
              description="请务必保存好密钥，如果更换手机或重装应用，需要使用密钥重新绑定。"
              type="warning"
              showIcon
            />

            <Row gutter={24}>
              <Col span={12}>
                <div style={{ textAlign: 'center' }}>
                  <Title level={5}>扫描二维码</Title>
                  {qrCodeUrl && (
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
                        qrCodeUrl
                      )}`}
                      alt="QR Code"
                      style={{
                        width: 200,
                        height: 200,
                        border: '1px solid #d9d9d9',
                        borderRadius: 4,
                      }}
                    />
                  )}
                  <Paragraph type="secondary" style={{ marginTop: 8 }}>
                    使用 Google Authenticator 扫描
                  </Paragraph>
                </div>
              </Col>

              <Col span={12}>
                <div>
                  <Title level={5}>或手动输入密钥</Title>
                  <Paragraph>
                    <Text strong>密钥:</Text>
                  </Paragraph>
                  <Input.Group compact>
                    <Input
                      value={secret}
                      readOnly
                      style={{ width: 'calc(100% - 80px)' }}
                    />
                    <Button icon={<CopyOutlined />} onClick={handleCopySecret}>
                      复制
                    </Button>
                  </Input.Group>
                  <Paragraph type="secondary" style={{ marginTop: 16 }}>
                    打开 Google Authenticator，选择"手动输入密钥"，然后输入上面的密钥。
                  </Paragraph>
                </div>
              </Col>
            </Row>

            <div style={{ textAlign: 'right' }}>
              <Space>
                <Button onClick={handleCancelBind}>
                  取消
                </Button>
                <Button type="primary" onClick={handleNextToVerify} size="large">
                  下一步，验证绑定
                </Button>
              </Space>
            </div>
          </Space>
        )}

        {/* 步骤3: 验证码确认 */}
        {bindStep === 2 && (
          <Form form={verifyForm} onFinish={handleFinishBind} layout="vertical">
            <Alert
              message="验证绑定"
              description="请打开 Google Authenticator，输入应用中显示的 6 位验证码以完成绑定。"
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Form.Item
              label="验证码"
              name="totpCode"
              rules={[
                { required: true, message: '请输入验证码' },
                { pattern: /^\d{6}$/, message: '验证码为 6 位数字' },
              ]}
            >
              <Input
                prefix={<KeyOutlined />}
                placeholder="请输入 Google Authenticator 中的 6 位验证码"
                maxLength={6}
                size="large"
                autoFocus
              />
            </Form.Item>

            <Alert
              message="注意"
              description="验证码每 30 秒更新一次，请确保输入的是最新的验证码。"
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={loading} size="large">
                  完成绑定
                </Button>
                <Button onClick={() => setBindStep(1)} size="large">
                  上一步
                </Button>
                <Button onClick={handleCancelBind}>
                  取消
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default TwoFactorAuth;
